import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/fmt";
import ReferenceBadge from "@/components/ReferenceBadge";
import { CurrencyEur, CheckCircle, Clock, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

// Driver-facing payment visibility (NOT accounting).
// Two buckets exposed by /api/driver/payments:
//   • to_pay   — completed rides not yet marked paid by admin (no time limit)
//   • paid_30d — rides marked paid within the last 30 days
// Refreshes on demand and when the user revisits the tab.
export default function DriverPayments() {
  const [data, setData] = useState({ to_pay: [], paid_30d: [], totals: {} });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/payments");
      setData(data || { to_pay: [], paid_30d: [], totals: {} });
    } catch {
      setData({ to_pay: [], paid_30d: [], totals: {} });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tot = data.totals || {};

  return (
    <div data-testid="driver-payments-section" className="space-y-4">
      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TotalCard
          testid="payments-total-to-pay"
          label="À payer"
          subtitle={`${tot.to_pay_count || 0} course${(tot.to_pay_count || 0) > 1 ? "s" : ""}`}
          amount={tot.to_pay || 0}
          color="amber"
          Icon={Clock}
        />
        <TotalCard
          testid="payments-total-paid"
          label="Payé récemment"
          subtitle={`${tot.paid_30d_count || 0} course${(tot.paid_30d_count || 0) > 1 ? "s" : ""}`}
          amount={tot.paid_30d || 0}
          color="emerald"
          Icon={CheckCircle}
        />
      </div>

      <div className="flex justify-end">
        <Button data-testid="payments-refresh" variant="outline" size="sm" onClick={fetchData}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8 px-2 text-xs">
          <ArrowsClockwise size={12} className="mr-1" /> Actualiser
        </Button>
      </div>

      {/* À payer */}
      <Section
        title="Courses à payer"
        emptyHint="Aucune course en attente de paiement."
        items={data.to_pay}
        loading={loading}
        testid="payments-to-pay-list"
        renderItem={(r) => <PaymentRow key={r.id} r={r} unpaid />}
      />

      {/* Payé récemment */}
      <Section
        title="Courses payées récemment"
        emptyHint="Aucune course payée récemment."
        items={data.paid_30d}
        loading={loading}
        testid="payments-paid-list"
        renderItem={(r) => <PaymentRow key={r.id} r={r} />}
      />

      <p className="text-[11px] text-zinc-600 text-center pt-1">
        Les paiements de plus de 30 jours sortent de cette vue mais restent dans votre historique.
      </p>
    </div>
  );
}

function TotalCard({ label, subtitle, amount, color, Icon, testid }) {
  const colors = {
    amber:  { bg: "bg-amber-500/10",  border: "border-amber-500/40",  text: "text-amber-400",  iconBg: "bg-amber-500/20" },
    emerald:{ bg: "bg-emerald-500/10",border: "border-emerald-500/40",text: "text-emerald-400",iconBg: "bg-emerald-500/20" },
  }[color] || {};
  return (
    <div data-testid={testid} className={`rounded-lg border p-4 ${colors.bg} ${colors.border}`}>
      <div className="flex items-start gap-3">
        <span className={`p-2 rounded-md ${colors.iconBg}`}>
          <Icon size={18} weight="bold" className={colors.text} />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] uppercase tracking-wider font-semibold ${colors.text}`}>{label}</p>
          <p className="text-2xl font-bold text-white tabular-nums mt-0.5">{amount.toFixed(2)} <span className="text-base text-zinc-400 font-medium">EUR</span></p>
          <p className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, items, loading, emptyHint, renderItem, testid }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{title}</h3>
      <div data-testid={testid} className="space-y-2">
        {loading && items.length === 0 ? (
          <div className="h-16 rounded bg-zinc-800/40 animate-pulse" />
        ) : items.length === 0 ? (
          <p className="text-xs text-zinc-600 italic py-3 text-center">{emptyHint}</p>
        ) : (
          items.map(renderItem)
        )}
      </div>
    </div>
  );
}

function PaymentRow({ r, unpaid }) {
  return (
    <div data-testid={`payment-row-${r.id}`} className="bg-[#121212] border border-zinc-800 rounded-md p-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <ReferenceBadge value={r.reference} size="xs" testid={`payment-row-ref-${r.id}`} />
          {unpaid ? (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 font-semibold">Non payé</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-semibold">Payé</span>
          )}
        </div>
        <p className="text-sm text-white truncate">{r.route_label || "—"}</p>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          {fmtDate(r.pickup_date)} · {r.pickup_time || ""}
          {r.paid_at && !unpaid && (
            <span className="ml-2 text-emerald-500/80">· payé le {new Date(r.paid_at).toLocaleDateString("fr-FR")}</span>
          )}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className={`text-base font-semibold tabular-nums ${unpaid ? "text-amber-400" : "text-emerald-400"}`}>
          <CurrencyEur size={12} weight="bold" className="inline align-text-top mr-0.5" />
          {(r.driver_fare || 0).toFixed(2)}
        </p>
      </div>
    </div>
  );
}
